import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { reviewsAPI } from '../lib/api';
import { StarRating } from '../components/ReviewsSection';
import { optimizeCloudinaryUrl } from '../lib/cloudinary';
import { toast } from 'sonner';
import { Star, Loader2, Send, Check, ShieldCheck, Store as StoreIcon, Package, ArrowLeft, Clock } from 'lucide-react';

const RatingSelector = ({ value, onChange, size = 30, targetId = '' }) => (
  <div className="inline-flex items-center gap-1">
    {[1, 2, 3, 4, 5].map((i) => (
      <button
        key={i}
        type="button"
        onClick={() => onChange(i)}
        className="transition-transform hover:scale-110 focus:outline-none rounded"
        aria-label={`${i} estrela${i > 1 ? 's' : ''}`}
        data-testid={`invite-star-${targetId}-${i}`}
      >
        <Star width={size} height={size} className={i <= value ? 'text-amber-400' : 'text-slate-300'} fill={i <= value ? 'currentColor' : 'none'} strokeWidth={1.75} />
      </button>
    ))}
  </div>
);

const TargetCard = ({ target, protocol, prefilledName, alreadyStatus, onSubmitted }) => {
  // target = { type: 'store'|'product', id, name, image_url }
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [name, setName] = useState(prefilledName || '');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(!!alreadyStatus);

  const submit = async () => {
    if (!name.trim()) { toast.error('Informe seu nome'); return; }
    if (comment.trim().length < 5) { toast.error('Escreva um comentário (mín. 5 caracteres)'); return; }
    setSubmitting(true);
    try {
      await reviewsAPI.create({
        target_type: target.type,
        target_id: target.id,
        rating,
        comment: comment.trim(),
        customer_name: name.trim(),
        order_protocol: protocol,
      });
      setDone(true);
      toast.success('Avaliação enviada! Ela aparecerá após moderação.');
      onSubmitted && onSubmitted();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar');
    } finally {
      setSubmitting(false);
    }
  };

  const isProduct = target.type === 'product';
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid={`invite-card-${target.type}-${target.id}`}>
      <div className="flex gap-3 p-4">
        <div className="w-20 h-20 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
          {target.image_url ? (
            <img src={optimizeCloudinaryUrl(target.image_url, { width: 200 })} alt={target.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-300">
              {isProduct ? <Package className="w-8 h-8" /> : <StoreIcon className="w-8 h-8" />}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
            isProduct ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'bg-purple-50 text-purple-700 border border-purple-200'
          }`}>
            {isProduct ? <Package className="w-3 h-3" /> : <StoreIcon className="w-3 h-3" />}
            {isProduct ? 'Produto' : 'Loja'}
          </span>
          <p className="font-semibold text-slate-900 mt-1 leading-tight line-clamp-2">{target.name}</p>
        </div>
      </div>

      {done ? (
        <div className="px-4 pb-4">
          <div className={`rounded-xl px-3 py-2 text-sm flex items-center gap-2 ${
            alreadyStatus === 'approved'
              ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
              : 'bg-amber-50 border border-amber-200 text-amber-800'
          }`} data-testid={`already-status-${target.id}`}>
            {alreadyStatus === 'approved' ? <Check className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
            {alreadyStatus === 'approved'
              ? 'Sua avaliação já foi aprovada e publicada. Obrigado!'
              : 'Sua avaliação já foi registrada e está aguardando moderação. Obrigado!'}
          </div>
        </div>
      ) : (
        <div className="px-4 pb-4 space-y-3 border-t pt-3">
          <div>
            <p className="text-xs font-semibold text-slate-700 mb-1">Sua nota</p>
            <RatingSelector value={rating} onChange={setRating} targetId={target.id} />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-700 mb-1">Seu nome</p>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Como você quer aparecer"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-400 focus:outline-none text-sm"
              data-testid={`invite-name-${target.id}`}
            />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-700 mb-1">Comentário</p>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={`Conte como foi sua experiência com ${isProduct ? 'esse produto' : 'essa loja'} (mín. 5 caracteres)`}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-400 focus:outline-none text-sm resize-none"
              data-testid={`invite-comment-${target.id}`}
            />
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-60"
            data-testid={`invite-submit-${target.id}`}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Enviar avaliação
          </button>
        </div>
      )}
    </div>
  );
};

export const ReviewInvite = () => {
  const { protocol } = useParams();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await reviewsAPI.reviewContext(protocol);
      setCtx(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Não foi possível carregar seu pedido.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [protocol]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
        <p className="text-slate-700 mb-3" data-testid="invite-error">{error}</p>
        <Link to="/" className="text-blue-600 font-semibold">Voltar à página inicial</Link>
      </div>
    );
  }

  if (!ctx.eligible) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center" data-testid="invite-not-eligible">
        <Clock className="w-10 h-10 text-amber-500 mb-3" />
        <p className="text-lg font-bold text-slate-900">Ainda não é hora de avaliar 🙂</p>
        <p className="text-sm text-slate-600 mt-1">
          Assim que seu pedido <strong>{ctx.protocol}</strong> for concluído, a avaliação será liberada aqui.
        </p>
        <Link to="/" className="mt-6 inline-block text-blue-600 font-semibold">Voltar à página inicial</Link>
      </div>
    );
  }

  const store = ctx.store;
  const products = ctx.products || [];
  const reviewed = ctx.reviewed || {};

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="invite-page">
      <header className="sticky top-0 z-40 bg-white border-b">
        <div className="max-w-2xl mx-auto flex items-center gap-3 px-4 py-3">
          <Link to="/" className="p-2 -ml-2 text-slate-700" aria-label="Voltar">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="min-w-0">
            <h1 className="font-bold text-slate-900 leading-tight">Avaliar sua compra</h1>
            <p className="text-xs text-slate-500 font-mono">{ctx.protocol}</p>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        <section className="rounded-2xl bg-gradient-to-br from-blue-600 to-violet-600 p-5 text-white shadow-lg">
          <div className="flex items-center gap-2 text-sm mb-2">
            <ShieldCheck className="w-5 h-5" />
            <span className="uppercase tracking-widest text-xs font-bold">Compra Verificada</span>
          </div>
          <h2 className="text-xl font-bold leading-tight">Olá{ctx.customer_name ? `, ${ctx.customer_name.split(' ')[0]}` : ''}! 👋</h2>
          <p className="text-sm text-white/90 mt-2">
            Seu feedback nos ajuda a evoluir. Dedique 30 segundos para avaliar cada produto e a loja. Obrigado!
          </p>
        </section>

        {store && (
          <TargetCard
            target={{ type: 'store', id: store.id, name: store.name, image_url: store.logo_url || store.banner_url || '' }}
            protocol={ctx.protocol}
            prefilledName={ctx.customer_name}
            alreadyStatus={reviewed[`store:${store.id}`]}
            onSubmitted={load}
          />
        )}
        {products.map((p) => (
          <TargetCard
            key={p.id}
            target={{ type: 'product', id: p.id, name: p.name, image_url: (p.images || [])[0] || '' }}
            protocol={ctx.protocol}
            prefilledName={ctx.customer_name}
            alreadyStatus={reviewed[`product:${p.id}`]}
            onSubmitted={load}
          />
        ))}

        <div className="text-center pt-3">
          <StarRating value={5} size={22} />
          <p className="text-xs text-slate-500 mt-2">Todas as avaliações passam por moderação antes de serem publicadas.</p>
        </div>
      </div>
    </div>
  );
};

export default ReviewInvite;
